/* MENASA Heritage: <menasa-heritage> (sections/menasa-heritage.liquid) and <menasa-journal> (sections/menasa-journal.liquid).
   The intro: the screen clears to the navbar's own colour and the navbar's MENASA lifts off. It grows to the middle as
   one word while a wave of weight runs through it, regroups as ME · NA · SA (three rows on phones), each pair's region
   rises into its letters, then the pictures drain away and the letters thin and close into the wordmark under the
   crest. The wordmark is the home link until the page scrolls; it then shrinks into the navbar's logo slot and the
   navbar's own logo takes over.
   Below it, text rises out of masks word by word (letter by letter for small labels) and pictures are uncovered by a
   moving window once they have loaded. Nothing appears or leaves at once: a tap during the intro dissolves it.
   Works with the site's page transitions (menasa-artistic-transitions.js): no inline scripts, the router hands over the
   navbar's look (window.menasaRouteFrom), Back shows the page as it rests, and leaving gives the header back. */
(() => {
  'use strict';
  if (customElements.get('menasa-heritage')) return;

  const root = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const phoneWidth = matchMedia('(max-width: 700px)');
  const GLASS_VARS = ['--nav-rgb', '--header-top', '--header-bottom'];
  const SEEN = 'menasa:heritage-intro';
  const IVORY = [250, 247, 239], INK = [33, 27, 23], NAVY = [19, 27, 43];
  // A section's tone under the navbar: [background, ink, glass opacity at the top].
  const TONES = { ivory: [IVORY, INK, .94], navy: [NAVY, IVORY, .9], study: [[27, 34, 29], IVORY, .9], desk: [[36, 23, 17], IVORY, .96] };
  // The variable face covers 300–700: the navbar's weight and the stage's.
  const THIN = 300, BOLD = 700;
  const SWEEP = 'cubic-bezier(.7,0,.16,1)', OUT = 'cubic-bezier(.22,.7,.16,1)', IN_OUT = 'cubic-bezier(.65,0,.35,1)';
  // The page's pace: every timing below is written at 1 and runs at this share of it (the stylesheet's are already
  // scaled to match). 0.7 = 30% quicker, as asked on 3 October 2026.
  const SPEED = .7;
  const pace = t => t * SPEED;
  const clamp = (v, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const mix = (a, b, t) => a.map((v, i) => lerp(v, b[i], t));
  const inOut = t => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const wait = ms => new Promise(r => setTimeout(r, ms));
  const rgbIn = s => {
    const m = /rgba?\(([^)]+)\)/.exec(s || '');
    if (!m) return null;
    const v = m[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(Number);
    return v.length === 3 && v.every(Number.isFinite) ? v : null;
  };
  // Text on a ground: whichever of the house's near-black and ivory reads with more contrast (WCAG luminance), so the
  // logo and words are dark on ivory or white grounds and ivory on dark ones, whatever the navbar's own ink was.
  const luminance = c => c.map(v => { v /= 255; return v <= .03928 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4); }).reduce((s, v, i) => s + v * [.2126, .7152, .0722][i], 0);
  const contrast = (a, b) => { const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p); return (x + .05) / (y + .05); };
  const isLight = c => contrast(c, [24, 27, 28]) >= contrast(c, [250, 247, 239]);
  const inkOn = c => (isLight(c) ? [24, 27, 28] : [250, 247, 239]);
  // Phones and the light effects tier: the letters cross-fade between their thin and bold copies instead of animating
  // the weight itself (which repaints every letter every frame).
  const lightTier = () => { const q = window.MenasaQuality; return !!(q && (q.phone || q.tier === 'light' || q.tier === 'reduced')); };

  // The router loads this file before it shows the page: start fetching the wordmark's face now.
  const warmFont = () => (document.fonts ? document.fonts.load(`${BOLD} 40px "MENASA Mark"`).catch(() => {}) : Promise.resolve());
  warmFont();

  // Wraps each word in a mask (.mh-w > .mh-wi) or each letter (.mh-c), numbered for the stagger. Text stays text, with
  // its spaces, so it reads and wraps as before.
  function split(el, chars) {
    if (el.mhParts) return el.mhParts;
    const parts = [], nodes = [];
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) if (n.textContent.trim()) nodes.push(n);
    for (const node of nodes) {
      const frag = document.createDocumentFragment();
      for (const token of node.textContent.split(/(\s+)/)) {
        if (!token) continue;
        if (!token.trim()) { frag.append(' '); continue; }
        if (chars) {
          const word = document.createElement('span');
          word.className = 'mh-cw';
          for (const c of token) {
            const s = document.createElement('span');
            s.className = 'mh-c';
            s.textContent = c;
            s.style.setProperty('--i', parts.length);
            parts.push(s);
            word.append(s);
          }
          frag.append(word);
        } else {
          const mask = document.createElement('span'), w = document.createElement('span');
          mask.className = 'mh-w';
          w.className = 'mh-wi';
          w.textContent = token;
          w.style.setProperty('--i', parts.length);
          parts.push(w);
          mask.append(w);
          frag.append(mask);
        }
      }
      node.replaceWith(frag);
    }
    el.mhParts = parts;
    return parts;
  }

  // The geometric ring around the codes' crest (as the Collection page draws its rings).
  function drawRing(svg) {
    if (svg.childElementCount) return;
    const ns = 'http://www.w3.org/2000/svg';
    const add = (name, attrs) => { const n = document.createElementNS(ns, name); for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v); svg.appendChild(n); };
    const pt = (a, r) => [Math.cos(a) * r, Math.sin(a) * r].map(v => v.toFixed(2));
    for (const r of [98, 95, 82, 60, 58, 30]) add('circle', { r, cx: 0, cy: 0 });
    for (let i = 0; i < 64; i++) { const a = i * Math.PI / 32; add('path', { d: 'M' + pt(a, 96).join(' ') + 'L' + pt(a, i % 4 === 0 ? 86 : 92).join(' ') }); }
    for (const o of [0, Math.PI / 4]) add('path', { d: 'M' + Array.from({ length: 8 }, (_, i) => pt(i * Math.PI / 4 + o, 75).join(' ')).join('L') + 'Z' });
    for (let i = 0; i < 8; i++) add('path', { d: 'M' + pt(i * Math.PI / 4, 90).join(' ') + 'L' + pt((i + 3) * Math.PI / 4, 90).join(' ') });
    for (let i = 0; i < 12; i++) add('path', { d: 'M' + pt(i * Math.PI / 6, 30).join(' ') + 'L' + pt(i * Math.PI / 6, 98).join(' ') });
  }

  // Each part rises once as it reaches the screen; a part holding pictures waits (up to 1.6 s) until they are decoded,
  // so a picture is never uncovered half-loaded. Without the script, or with reduced motion, everything is simply there.
  let observer = null;
  function show(el) {
    const images = [...el.querySelectorAll('img')].filter(img => !img.complete || !img.naturalWidth);
    images.forEach(img => { img.loading = 'eager'; });
    const ready = images.length ? Promise.race([Promise.all(images.map(img => (img.decode ? img.decode() : Promise.resolve()).catch(() => {}))), wait(1600)]) : Promise.resolve();
    ready.then(() => requestAnimationFrame(() => el.classList.add('is-in')));
  }
  function reveal(scope) {
    if (scope.mhRevealed) return;
    scope.mhRevealed = true;
    if (reduce.matches || !('IntersectionObserver' in window)) return;
    // Phones and the light tier: small labels rise word by word too (letter by letter is dozens of moving layers at once).
    const chars = !lightTier();
    scope.querySelectorAll('[data-split]').forEach(el => split(el, chars && el.dataset.split === 'chars'));
    observer = observer || new IntersectionObserver(entries => entries.forEach(e => {
      if (!e.isIntersecting) return;
      observer.unobserve(e.target);
      show(e.target);
    }), { rootMargin: '0px 0px -10% 0px', threshold: .1 });
    scope.classList.add('js-reveal');
    scope.querySelectorAll('[data-in]').forEach(el => {
      if (el.getBoundingClientRect().bottom < 0) el.classList.add('is-in');
      else observer.observe(el);
    });
  }

  // Each character's advance-box centre (its letter-spacing removed) and the font size: how the navbar's MENASA, the
  // stage and the wordmark are matched letter for letter.
  function glyphs(el) {
    if (!el) return null;
    const cs = getComputedStyle(el), spacing = parseFloat(cs.letterSpacing) || 0, list = [];
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT), range = document.createRange();
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = node.textContent;
      for (let i = 0; i < text.length; i++) {
        if (!text[i].trim()) continue;
        range.setStart(node, i);
        range.setEnd(node, i + 1);
        const r = range.getBoundingClientRect();
        if (r.width) list.push({ x: r.left + (r.width - spacing) / 2, y: r.top + r.height / 2 });
      }
    }
    return list.length ? { size: parseFloat(cs.fontSize), list } : null;
  }

  class MenasaHeritage extends HTMLElement {
    connectedCallback() {
      if (this.alive) return;
      this.alive = true;
      this.off = [];
      this.anims = [];
      this.state = 'settled';
      MenasaHeritage.active = this;
      this.header = document.getElementById('PrestigeHeader');
      this.logo = this.header && this.header.querySelector('.header-logo a');
      this.open = this.querySelector('.mh-open');
      this.mark = this.querySelector('.mh-mark');
      this.overlay = this.querySelector('.mh-intro');
      this.stage = this.querySelector('.mh-stage');
      this.letters = [...this.querySelectorAll('.mh-l')];
      this.pairs = [...this.querySelectorAll('.mh-pair')];
      this.fading = [...this.querySelectorAll('.mh-crest, .mh-rule, .mh-motto, .mh-foot, .mh-whisper')];
      this.querySelectorAll('svg[data-ring]').forEach(drawRing);

      // The arrival colour: the navbar as it was when Heritage was chosen (handed over by the router), or as it is now.
      const from = window.menasaRouteFrom;
      this.routed = !!(from && performance.now() - from.at < 5000);
      const glass = this.header && this.header.querySelector('.site-header__glass');
      this.bg = rgbIn(this.routed ? from.glass : glass && getComputedStyle(glass, '::after').backgroundImage) || IVORY;
      // The ink follows the ground, not the navbar's old ink (which can be light over a see-through navbar on a photo,
      // or halfway between two tones when the visitor left mid-scroll).
      this.ink = inkOn(this.bg);
      this.style.setProperty('--arr-bg', this.bg.join(' '));
      this.style.setProperty('--arr-ink', this.ink.join(' '));
      if (!isLight(this.bg)) this.open.style.setProperty('--accent', 'var(--bronze-soft)');
      root.classList.add('mh-on');

      this.listen();
      this.codes();
      // The router scrolls to the top after inserting the page: decide once its swap has finished.
      const back = this.routed ? from.type === 'traverse' : (performance.getEntriesByType('navigation')[0] || {}).type === 'back_forward';
      queueMicrotask(() => {
        if (!this.alive) return;
        reveal(this);
        let seen = false;
        try { seen = sessionStorage.getItem(SEEN) === '1'; } catch (e) { /* storage blocked: play */ }
        const mode = this.dataset.intro;
        const play = this.hasAttribute('data-armed') && mode !== 'off' && !(mode === 'session' && seen) && !back
          && !reduce.matches && !(window.Shopify && window.Shopify.designMode) && scrollY < 40 && !location.hash;
        // Whatever goes wrong inside the intro, the page settles rather than staying behind it.
        if (play) this.play().catch(() => this.settle()); else this.settle();
      });
      // The router clears the header's colours right after inserting the page; paint ours on the next frame.
      requestAnimationFrame(() => { if (this.alive) { this.measure(); this.inkS = this.glassKey = this.gS = undefined; this.onScroll(); } });
    }

    disconnectedCallback() { this.stop(); }

    stop() {
      if (!this.alive) return;
      this.alive = false;
      this.state = 'off';
      this.off.forEach(f => f());
      this.off = [];
      this.anims.forEach(a => a.cancel());
      this.anims = [];
      cancelAnimationFrame(this.raf);
      cancelAnimationFrame(this.mraf);
      if (MenasaHeritage.active === this) {
        MenasaHeritage.active = null;
        root.classList.remove('mh-on', 'mh-hush', 'mh-logo-away');
        if (this.header) this.header.style.removeProperty('--nav-ink');
        if (this.pane) { GLASS_VARS.forEach(n => this.pane.style.removeProperty(n)); this.pane.style.removeProperty('opacity'); }
      }
    }

    on_(target, type, fn, opts) { target.addEventListener(type, fn, opts); this.off.push(() => target.removeEventListener(type, fn, opts)); }

    listen() {
      this.on_(window, 'scroll', () => this.queue(), { passive: true });
      this.lastW = innerWidth;
      this.on_(window, 'resize', () => {
        // Phones resize when their address bar slides: only a width change ends the intro.
        if (innerWidth !== this.lastW) { this.lastW = innerWidth; this.finish(); }
        this.measureSoon();
      }, { passive: true });
      this.on_(phoneWidth, 'change', () => this.measureSoon());
      this.on_(document, 'menasa:quality', () => this.measureSoon());
      this.on_(reduce, 'change', () => { if (reduce.matches) this.finish(); });
      if (document.fonts && document.fonts.status !== 'loaded') document.fonts.ready.then(() => { if (this.alive) this.measureSoon(); });
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(() => this.measureSoon());
        ro.observe(this);
        this.off.push(() => ro.disconnect());
      }
      // Leaving by the site's page transition: give the header back before the next page reads it.
      this.on_(document, 'shopify:section:unload', e => { if (e.target && e.target.contains && e.target.contains(this)) this.stop(); });
      // Theme Editor: selecting this section plays the intro again.
      this.on_(document, 'shopify:section:select', e => { if (e.target && e.target.contains && e.target.contains(this)) this.replay(); });
      // The scroll link glides to the first section.
      const cue = this.querySelector('.mh-cue');
      if (cue) this.on_(cue, 'click', e => {
        const target = document.getElementById(cue.getAttribute('href').slice(1));
        if (!target) return;
        e.preventDefault();
        scrollTo({ top: target.getBoundingClientRect().top + scrollY - 1, behavior: reduce.matches ? 'auto' : 'smooth' });
      });
    }

    /* ── Geometry ──────────────────────────────────────────────────────────────────────────────── */
    measureSoon() {
      cancelAnimationFrame(this.mraf);
      this.mraf = requestAnimationFrame(() => { if (this.alive) { this.measure(); this.onScroll(); } });
    }

    measure() {
      // Docking: the wordmark at rest, and the navbar's logo slot (fixed in the viewport).
      const m = this.mark;
      m.style.transform = '';
      this.lastT = undefined;
      const mr = m.getBoundingClientRect(), ms = getComputedStyle(m), mSpace = parseFloat(ms.letterSpacing) || 0;
      const ls = this.logo && getComputedStyle(this.logo), lr = this.logo && this.logo.getBoundingClientRect();
      const lSpace = ls ? parseFloat(ls.letterSpacing) || 0 : 0;
      const mx = mr.left + (mr.width - mSpace) / 2, my = mr.top + mr.height / 2 + scrollY;
      const nx = lr && lr.width ? lr.left + (lr.width - lSpace) / 2 : innerWidth / 2;
      const ny = lr && lr.height ? lr.top + lr.height / 2 : 40;
      this.dock = {
        // The wordmark scrolls with the page; after this much scrolling its centre is on the logo's.
        travel: Math.max(1, my - ny),
        dx: nx - mx,
        k: ls ? parseFloat(ls.fontSize) / parseFloat(ms.fontSize) : .3
      };
      m.style.transformOrigin = `${((mr.width - mSpace) / 2).toFixed(2)}px 50%`;
      this.hh = this.header ? this.header.offsetHeight : 72;
      this.tones = [...document.querySelectorAll('#MainContent [data-mh-tone]')]
        .filter(el => el.dataset.mhTone !== 'arrival')
        .map(el => ({ tone: TONES[el.dataset.mhTone] || TONES.ivory, top: el.getBoundingClientRect().top + scrollY }));
    }

    // The stage fits the screen (86% of the width; on phones the three rows share the height) and each letter keeps
    // the box of its bold self, so nothing shifts while the weight changes.
    fit() {
      const st = this.stage;
      st.style.fontSize = '';
      this.letters.forEach(l => { l.style.width = ''; });
      let size = parseFloat(getComputedStyle(st).fontSize);
      const row = getComputedStyle(st).flexDirection !== 'column';
      const boxes = this.pairs.map(p => p.getBoundingClientRect());
      const w = row ? boxes[boxes.length - 1].right - boxes[0].left : Math.max(...boxes.map(b => b.width));
      const h = row ? boxes[0].height : boxes.reduce((a, b) => a + b.height, 0);
      const k = Math.min(1, innerWidth * .86 / w, innerHeight * (row ? .5 : .66) / h);
      if (k < 1) { size *= k; st.style.fontSize = size.toFixed(2) + 'px'; }
      this.stageSize = size;
      this.letters.forEach(l => { l.style.width = l.getBoundingClientRect().width.toFixed(3) + 'px'; });
    }

    /* ── Scroll: the wordmark docks into the navbar, the header takes the tone under it ─────────── */
    queue() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = 0; this.onScroll(); });
    }

    onScroll() {
      if (!this.alive || !this.dock) return;
      const y = scrollY;
      if (this.state === 'intro') {
        if (y > 4) this.finish();
        else { this.tone(0); return; }
      }
      const p = clamp(y / this.dock.travel), e = inOut(p), docked = p >= 1;
      if (docked !== this.docked) {
        this.docked = docked;
        // At the logo's place and size: the navbar's own logo takes over, and gives it back on the way up.
        this.mark.style.visibility = docked ? 'hidden' : '';
        root.classList.toggle('mh-logo-away', !docked);
        this.open.classList.toggle('is-away', docked);
      }
      const t = docked ? '' : `translate3d(${(this.dock.dx * e).toFixed(2)}px,0,0) scale(${lerp(1, this.dock.k, e).toFixed(4)})`;
      if (t !== this.lastT) { this.lastT = t; this.mark.style.transform = t; }
      const fade = (1 - clamp(p * 1.7)).toFixed(3);
      if (fade !== this.lastFade) { this.lastFade = fade; this.fading.forEach(el => { el.style.opacity = fade === '1.000' ? '' : fade; }); }
      // The navbar's glass returns as the wordmark nears it.
      this.tone(clamp((p - .45) / .55));
    }

    tone(glass) {
      if (!this.header || !this.tones) return;
      const y = scrollY, band = this.hh;
      let rgb = this.bg, ink = this.ink, top = .92, g = glass;
      for (const s of this.tones) {
        const cover = clamp((y + band - s.top) / band);
        if (cover <= 0) continue;
        rgb = mix(rgb, s.tone[0], cover);
        ink = mix(ink, s.tone[1], cover);
        top = lerp(top, s.tone[2], cover);
        g = lerp(g, 1, cover);
      }
      // Only what changed, and the glass's own colours on the glass (writing them on the header restyles all of it).
      const inkS = ink.map(Math.round).join(' '), glassKey = rgb.map(Math.round).join(' ') + '|' + top.toFixed(2), gS = g.toFixed(2);
      let pane = this.pane;
      if (!pane || !pane.isConnected) {
        // The header rebuilds its glass when the effects tier changes (assets/menasa-header.js).
        pane = this.pane = this.header.querySelector('.site-header__glass');
        this.glassKey = this.gS = undefined;
      }
      if (inkS !== this.inkS) { this.inkS = inkS; this.header.style.setProperty('--nav-ink', inkS); }
      if (!pane) return;
      if (glassKey !== this.glassKey) {
        this.glassKey = glassKey;
        pane.style.setProperty('--nav-rgb', glassKey.split('|')[0]);
        pane.style.setProperty('--header-top', top.toFixed(2));
        pane.style.setProperty('--header-bottom', (top * .82).toFixed(2));
      }
      if (gS !== this.gS) { this.gS = gS; pane.style.opacity = gS === '1.00' ? '' : gS; }
    }

    /* ── 02 The codes: an index and one code at a time, the crest lighting its part ───────────────── */
    codes() {
      const sec = this.querySelector('.mh-codes');
      if (!sec) return;
      const tabs = [...sec.querySelectorAll('.mh-tab')], panels = [...sec.querySelectorAll('.mh-detail')];
      if (!tabs.length) return;
      let current = 0;
      // One frame holds them all: hidden panels stay in the frame's grid cell, out of reach.
      panels.forEach((p, i) => { p.hidden = false; p.inert = i !== 0; p.setAttribute('aria-hidden', String(i !== 0)); });
      // 786's digits rise one by one when its code is chosen.
      const number = sec.querySelector('.mh-786 b');
      if (number && !reduce.matches) split(number, true);
      sec.style.setProperty('--k', '0');
      const select = (i, focus) => {
        i = (i + tabs.length) % tabs.length;
        if (i === current) { if (focus) tabs[i].focus(); return; }
        const from = panels[current], to = panels[i];
        tabs.forEach((t, k) => { t.setAttribute('aria-selected', String(k === i)); t.tabIndex = k === i ? 0 : -1; });
        if (focus) tabs[i].focus();
        // The crest zooms to the code's part (stylesheet, by data-code); the ring turns to the code's place.
        sec.dataset.code = tabs[i].dataset.code || '';
        sec.style.setProperty('--k', String(i));
        // The old words leave upwards; the new ones start below (no transition for that reset) and rise after them.
        from.classList.remove('is-on');
        from.classList.add('is-out');
        from.inert = true;
        from.setAttribute('aria-hidden', 'true');
        clearTimeout(from.mhTimer);
        from.mhTimer = setTimeout(() => from.classList.remove('is-out'), pace(650));
        clearTimeout(to.mhTimer);
        to.classList.add('no-motion');
        to.classList.remove('is-out');
        void to.offsetWidth;
        to.classList.remove('no-motion');
        to.classList.add('is-on');
        to.inert = false;
        to.setAttribute('aria-hidden', 'false');
        current = i;
      };
      tabs.forEach((t, i) => {
        this.on_(t, 'click', () => select(i));
        this.on_(t, 'keydown', e => {
          const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
          if (step) { e.preventDefault(); select(current + step, true); }
          else if (e.key === 'Home') { e.preventDefault(); select(0, true); }
          else if (e.key === 'End') { e.preventDefault(); select(tabs.length - 1, true); }
        });
      });
      // A sideways swipe on the frame turns to the next or previous code; scrolling up and down is left alone.
      const frame = sec.querySelector('[data-swipe]');
      if (frame) {
        let sx = 0, sy = 0, down = false;
        this.on_(frame, 'pointerdown', e => { if (e.pointerType === 'mouse') return; down = true; sx = e.clientX; sy = e.clientY; });
        this.on_(frame, 'pointerup', e => {
          if (!down) return;
          down = false;
          const dx = e.clientX - sx, dy = e.clientY - sy;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) select(current + (dx < 0 ? 1 : -1));
        });
        this.on_(frame, 'pointercancel', () => { down = false; });
      }
      // Autoplay (9 Oct 2026): while the codes are on screen they turn on their own, one every few seconds, the crest
      // lighting each part in turn. Any touch, click or key inside the codes hands control to the visitor for good.
      const every = +sec.dataset.autoplay;
      if (every > 0 && !reduce.matches && !(window.Shopify && window.Shopify.designMode) && 'IntersectionObserver' in window) {
        let timer = 0, seen = false, manual = false;
        const halt = () => { clearInterval(timer); timer = 0; };
        const run = () => { halt(); if (!manual && seen && !document.hidden) timer = setInterval(() => select(current + 1), every * 1000); };
        // "on screen": crossing the middle of the viewport, however tall the section is
        const watch = new IntersectionObserver(([e]) => { seen = e.isIntersecting; run(); }, { rootMargin: '-35% 0px -35% 0px' });
        watch.observe(sec);
        const take = () => { manual = true; halt(); };
        this.on_(sec, 'pointerdown', take);
        this.on_(sec, 'keydown', take);
        this.on_(document, 'visibilitychange', run);
        this.off.push(() => { halt(); watch.disconnect(); });
      }
      // Every code's picture loads before the codes are reached, so none arrives half-drawn.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(entries => {
          if (!entries.some(e => e.isIntersecting)) return;
          io.disconnect();
          sec.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
        }, { rootMargin: '800px 0px' });
        io.observe(sec);
        this.off.push(() => io.disconnect());
      }
      // Theme Editor: selecting a Code block shows it.
      this.on_(document, 'shopify:block:select', e => {
        const k = tabs.indexOf(e.target);
        if (k >= 0) select(k);
      });
    }

    /* ── The intro ──────────────────────────────────────────────────────────────────────────────── */
    A(el, frames, options) {
      const a = el.animate(frames, Object.assign({ fill: 'both' }, options));
      // Durations and delays are written at the original pace; the animation runs at the page's.
      a.playbackRate = 1 / SPEED;
      this.anims.push(a);
      return a;
    }

    // Phones and the light tier: a letter thickens (or thins) by cross-fading its bold and thin copies.
    thicken(l, bolder, duration, delay) {
      const bold = l.firstElementChild, thin = l.lastElementChild, a = bolder ? 0 : 1, b = 1 - a;
      if (!bold || bold === thin) return;
      this.A(bold, [{ opacity: a }, { offset: .3, opacity: a }, { offset: .8, opacity: b }, { opacity: b }], { duration, delay, easing: 'linear' });
      this.A(thin, [{ opacity: b }, { offset: .3, opacity: b }, { offset: .8, opacity: a }, { opacity: a }], { duration, delay, easing: 'linear' });
    }

    async play() {
      this.state = 'intro';
      this.classList.add('is-intro');
      root.classList.add('mh-hush');
      try { if (this.dataset.intro === 'session') sessionStorage.setItem(SEEN, '1'); } catch (e) { /* storage blocked */ }
      const bg = this.overlay.querySelector('.mh-intro__bg');
      // Arriving from another page, the screen clears over it; on a first load it is clear from the start.
      bg.style.opacity = this.routed ? '0' : '';
      this.overlay.style.opacity = '';
      if (!this.skipping) {
        this.skipping = true;
        const skip = () => this.finish();
        for (const type of ['wheel', 'touchmove', 'keydown']) this.on_(window, type, skip, { passive: true });
        this.on_(this.overlay, 'pointerdown', skip);
        this.on_(document, 'visibilitychange', () => { if (document.hidden) skip(); });
      }

      // The region pictures as the stylesheet picks them (phones get the smaller ones), decoded before they are shown.
      const fills = this.pairs.map(p => p.querySelector('.mh-fill'));
      const pictures = fills.map(f => f && (/url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(f.firstElementChild).backgroundImage) || [])[1]);
      const decoded = Promise.all(pictures.map(src => {
        if (!src) return Promise.reject(new Error('No picture'));
        const image = new Image();
        image.src = src;
        return image.decode();
      })).then(() => true, () => false);
      // The region names and the motto rise letter by letter and word by word.
      const tags = this.pairs.map(p => { const t = p.querySelector('.mh-tag > span'); return t ? split(t, !lightTier()) : []; });
      const motto = this.querySelector('.mh-motto');
      const mottoWords = motto ? split(motto, false) : [];

      await Promise.race([warmFont(), wait(1000)]);
      if (this.state !== 'intro') return;
      this.fit();
      const light = lightTier();
      const t0 = performance.now();
      // until: a moment on the real clock; at: a moment on the intro's own (original-pace) timeline.
      const until = real => wait(Math.max(0, t0 + real - performance.now()));
      const at = t => until(pace(t));
      const size = this.stageSize;
      const rest = this.letters.map(l => { const r = l.getBoundingClientRect(); return { x: r.left + r.width / 2, y: r.top + r.height / 2 }; });

      // 1 · The navbar's MENASA lifts off. It travels to the middle of the screen as one word, growing, while a wave of
      //     weight runs through it letter by letter; then its letters regroup into ME · NA · SA.
      const nav = glyphs(this.logo);
      const matched = nav && nav.list.length === this.letters.length;
      const navSize = nav ? nav.size : 30;
      const navMid = nav ? { x: (nav.list[0].x + nav.list[nav.list.length - 1].x) / 2, y: nav.list[0].y } : { x: innerWidth / 2, y: 40 };
      const navSpan = matched ? nav.list[nav.list.length - 1].x - nav.list[0].x : 0;
      // The word in the middle: as large as the stage's letters, at most 80% of the width.
      const midSize = Math.min(size, navSpan ? innerWidth * .8 / (navSpan / navSize + .9) : size);
      const midY = rest.reduce((a, r) => a + r.y, 0) / rest.length;
      const pose = (x, y, scale) => `translate(${x.toFixed(2)}px,${y.toFixed(2)}px) scale(${scale.toFixed(4)})`;
      this.letters.forEach((l, i) => {
        const from = matched ? nav.list[i] : navMid;
        const mid = { x: innerWidth / 2 + (from.x - navMid.x) * midSize / navSize, y: phoneWidth.matches ? innerHeight / 2 : midY };
        this.A(l, [
          { transform: pose(from.x - rest[i].x, from.y - rest[i].y, navSize / size), easing: 'cubic-bezier(.66,0,.24,1)' },
          { offset: .64, transform: pose(mid.x - rest[i].x, mid.y - rest[i].y, midSize / size), easing: 'cubic-bezier(.55,0,.15,1)' },
          { transform: pose(0, 0, 1) }
        ], { duration: 1650, delay: 80 });
        if (light) this.thicken(l, true, 1300, 220 + i * 60);
        else this.A(l, [{ fontWeight: THIN }, { fontWeight: BOLD }], { duration: 1150, delay: 260 + i * 60, easing: 'cubic-bezier(.45,0,.2,1)' });
      });
      // The letters show exactly where the logo is, and the logo steps out in the same frame.
      this.classList.add('is-run');
      root.classList.add('mh-logo-away');
      if (this.routed) this.A(bg, [{ opacity: 0 }, { opacity: 1 }], { duration: 560, easing: 'cubic-bezier(.4,0,.2,1)' });

      // 2 · The dots, and each pair's region name rising letter by letter under it.
      const dots = [...this.querySelectorAll('.mh-dot')];
      dots.forEach((d, i) => this.A(d, [{ transform: 'scale(0)' }, { transform: 'scale(1)' }], { duration: 700, delay: 1420 + i * 90, easing: 'cubic-bezier(.34,1.5,.64,1)' }));
      tags.forEach((chars, k) => chars.forEach((c, j) => this.A(c, [{ transform: 'translateY(130%)' }, { transform: 'translateY(0)' }], { duration: 800, delay: 1560 + k * 120 + j * 22, easing: OUT })));

      // 3 · Each pair's region rises into its letters (a window moving up while the picture-text holds still), as soon
      //     as the pictures are decoded (up to 3.4 s in; on a slow first visit the letters stay ink and the intro goes on).
      const ready = await Promise.race([decoded, until(3400).then(() => false)]);
      if (this.state !== 'intro') return;
      await at(1780);
      if (this.state !== 'intro') return;
      const filled = performance.now() - t0;
      if (ready) {
        this.pairs.forEach((p, k) => {
          const win = fills[k], delay = k * 150;
          if (!win) return;
          this.A(win, [{ transform: 'translateY(101%)' }, { transform: 'translateY(0%)' }], { duration: 1150, delay, easing: SWEEP });
          this.A(win.firstElementChild, [{ transform: 'translateY(-101%)' }, { transform: 'translateY(0%)' }], { duration: 1150, delay, easing: SWEEP });
          // The ink under the picture steps back once it is covered, so no dark edge rims the letters.
          p.querySelectorAll('.mh-l').forEach(l => this.A(l, [{ opacity: 1 }, { opacity: 0 }], { duration: 500, delay: delay + 950, easing: 'ease-out' }));
        });
      }

      // 4 · A held moment, then the pictures drain upwards out of the letters and the letters thin and close into the
      //     wordmark under the crest.
      await (ready ? until(filled + pace(1900)) : at(2500));
      if (this.state !== 'intro') return;
      if (ready) this.pairs.forEach((p, k) => {
        const win = fills[k], delay = k * 80;
        p.querySelectorAll('.mh-l').forEach(l => this.A(l, [{ opacity: 0 }, { opacity: 1 }], { duration: 300, delay, easing: 'linear' }));
        if (!win) return;
        this.A(win, [{ transform: 'translateY(0%)' }, { transform: 'translateY(-101%)' }], { duration: 800, delay: delay + 120, easing: IN_OUT });
        this.A(win.firstElementChild, [{ transform: 'translateY(0%)' }, { transform: 'translateY(101%)' }], { duration: 800, delay: delay + 120, easing: IN_OUT });
      });
      tags.forEach((chars, k) => chars.forEach((c, j) => this.A(c, [{ transform: 'translateY(0)' }, { transform: 'translateY(-130%)' }], { duration: 520, delay: k * 60 + j * 10, easing: IN_OUT })));
      dots.forEach(d => this.A(d, [{ transform: 'scale(1)' }, { transform: 'scale(0)' }], { duration: 450, easing: IN_OUT }));
      await wait(pace(ready ? 620 : 160));
      if (this.state !== 'intro') return;

      const mark = glyphs(this.mark);
      if (!mark) { this.finish(); return; }
      const s1 = mark.size / size, middle = (this.letters.length - 1) / 2;
      let latest = 0;
      this.letters.forEach((l, i) => {
        const to = mark.list[Math.min(i, mark.list.length - 1)];
        const delay = Math.abs(i - middle) * 45;
        latest = Math.max(latest, delay);
        this.A(l, [
          { transform: 'translate(0px,0px) scale(1)' },
          { transform: `translate(${(to.x - rest[i].x).toFixed(2)}px,${(to.y - rest[i].y).toFixed(2)}px) scale(${s1.toFixed(4)})` }
        ], { duration: 1150, delay, easing: 'cubic-bezier(.7,0,.16,1)' });
        if (light) this.thicken(l, false, 1000, delay + 60);
        else this.A(l, [{ fontWeight: BOLD }, { fontWeight: THIN }], { duration: 1000, delay: delay + 60, easing: 'cubic-bezier(.45,0,.25,1)' });
      });
      // The overlay's ground gives way to the page's own (the same colour); the lockup arrives around the letters:
      // the crest uncovered from below, the rule drawn outwards, the motto word by word.
      this.A(bg, [{ opacity: 1 }, { opacity: 0 }], { duration: 600, delay: 250, easing: 'linear' });
      const crest = this.querySelector('.mh-crest'), rule = this.querySelector('.mh-rule');
      const arriving = [];
      // On phones the letters cross the crest's place on their way from the rows: it waits for them.
      const later = ms => (phoneWidth.matches ? ms + 380 : ms);
      if (crest) arriving.push(this.A(crest, [{ opacity: 0, clipPath: 'inset(100% 0 0 0)', transform: 'translateY(16px)' }, { opacity: 1, clipPath: 'inset(0% 0 0 0)', transform: 'none' }], { duration: 1300, delay: later(420), easing: SWEEP }));
      if (rule) arriving.push(this.A(rule, [{ opacity: 0, transform: 'scaleX(0)' }, { opacity: .4, transform: 'scaleX(1)' }], { duration: 1000, delay: later(860), easing: IN_OUT }));
      mottoWords.forEach((w, i) => arriving.push(this.A(w, [{ opacity: 0, transform: 'translateY(118%) rotate(5deg)' }, { opacity: 1, transform: 'none' }], { duration: 1000, delay: later(950) + i * 70, easing: OUT })));
      this.querySelectorAll('.mh-foot, .mh-whisper').forEach(el => arriving.push(this.A(el, [{ opacity: 0, transform: el.matches('.mh-whisper') ? 'translateY(calc(-50% + 12px))' : 'translateY(12px)' }, { opacity: el.matches('.mh-whisper') ? .6 : 1, transform: el.matches('.mh-whisper') ? 'translateY(-50%)' : 'none' }], { duration: 1100, delay: later(1250), easing: OUT })));

      await wait(pace(1150 + latest) + 16);
      if (this.state !== 'intro') return;
      // 5 · The wordmark takes the letters' place, the overlay goes, the navbar's links and icons return.
      this.classList.add('is-marked');
      this.removeAttribute('data-armed');
      root.classList.remove('mh-hush');
      // Timers, not only animation events: a tab that stops painting must not leave the page mid-intro.
      await Promise.race([Promise.all(arriving.map(a => a.finished.catch(() => {}))), wait(pace(1800))]);
      if (this.state !== 'intro') return;
      this.settle();
    }

    // The page as it rests: no overlay, the lockup in place, the wordmark docking with the scroll.
    settle() {
      this.state = 'settled';
      this.anims.forEach(a => a.cancel());
      this.anims = [];
      this.removeAttribute('data-armed');
      this.classList.remove('is-intro', 'is-run', 'is-marked');
      this.overlay.style.opacity = '';
      root.classList.remove('mh-hush');
      this.docked = undefined;
      this.lastT = this.lastFade = undefined;
      if (this.dock) this.onScroll();
    }

    // Any scroll, tap or key during the intro: the intro dissolves over the page as it rests (nothing jumps).
    finish() {
      if (this.state !== 'intro') return;
      this.state = 'leaving';
      const overlayAnims = this.anims.filter(a => a.effect && this.overlay.contains(a.effect.target));
      this.anims.filter(a => !overlayAnims.includes(a)).forEach(a => a.cancel());
      this.anims = overlayAnims;
      this.classList.add('is-marked');
      this.classList.remove('is-intro');
      root.classList.remove('mh-hush');
      if (reduce.matches) { this.settle(); return; }
      // The lockup fades up under the overlay while the overlay (letters and ground) fades away.
      [this.mark, ...this.fading].forEach(el => el.animate([{ opacity: 0 }, {}], { duration: pace(520), easing: 'ease-out' }));
      const out = this.overlay.animate([{ opacity: getComputedStyle(this.overlay).opacity }, { opacity: 0 }], { duration: pace(480), easing: 'ease-out', fill: 'forwards' });
      this.anims.push(out);
      out.finished.catch(() => {}).then(() => { if (this.state === 'leaving') this.settle(); });
      setTimeout(() => { if (this.state === 'leaving') this.settle(); }, pace(700));
      this.docked = undefined;
      this.onScroll();
    }

    // Plays the intro again from the top (the Theme Editor does this when the section is selected).
    replay() {
      if (!this.alive || this.state !== 'settled' || reduce.matches) return;
      this.anims.forEach(a => a.cancel());
      this.anims = [];
      scrollTo({ top: 0, behavior: 'instant' });
      this.routed = false;
      this.mark.style.transform = '';
      this.mark.style.visibility = '';
      this.fading.forEach(el => { el.style.opacity = ''; });
      this.lastT = this.lastFade = undefined;
      this.setAttribute('data-armed', '');
      this.measure();
      this.play().catch(() => this.settle());
    }
  }
  window.MenasaHeritage = { replay: () => MenasaHeritage.active && MenasaHeritage.active.replay() };

  // A post's own HTML (written in Shopify's editor): text blocks rise word by word and pictures are uncovered, block
  // after block. On an entry's own page each block rises as it reaches the screen.
  function prepareContent(box, onScroll) {
    if (box.mhPrepared) return;
    box.mhPrepared = true;
    let d = 0;
    for (const block of [...box.children]) {
      const images = [...block.querySelectorAll('img')];
      for (const img of images) {
        if (img.parentElement.classList.contains('mh-sweep')) continue;
        const sweep = document.createElement('span');
        sweep.className = 'mh-sweep';
        img.replaceWith(sweep);
        sweep.append(img);
        if (!img.hasAttribute('loading')) img.loading = 'lazy';
      }
      if (images.length > 1) block.classList.add('is-pair');
      // The editor puts pictures inside paragraphs: such a paragraph is laid out as a figure.
      if (images.length && block.matches('p')) block.classList.add('mh-figure');
      const words = !images.length && block.matches('p, blockquote, h2, h3, h4, ul, ol') ? [block] : [...block.querySelectorAll('figcaption')];
      words.forEach(el => { el.setAttribute('data-split', ''); split(el, false); });
      if (onScroll) block.setAttribute('data-in', '');
      else block.style.setProperty('--d', d.toFixed(2));
      d += images.length ? .22 : .1;
    }
  }

  // Brings an element's top back under the navbar when it has scrolled away above it.
  function keepInView(el) {
    const header = document.getElementById('PrestigeHeader'), top = header ? header.offsetHeight : 72;
    const r = el.getBoundingClientRect();
    if (r.top < top) scrollTo({ top: r.top + scrollY - top - 16, behavior: reduce.matches ? 'auto' : 'smooth' });
  }

  // Desk only: a post's pictures become mounted prints. Each run of picture-only blocks (a <figure>, or a paragraph
  // holding nothing but images) turns into one spread: two prints side by side in a waterfall, a single picture full
  // width, the captions under the spread. The editor's order is kept; nothing changes in the post itself.
  function deskPlates(box) {
    if (box.mhPlates) return;
    box.mhPlates = true;
    const pictureOnly = el => {
      if (!/^(FIGURE|P|DIV)$/.test(el.tagName) || !el.querySelector('img')) return false;
      const copy = el.cloneNode(true);
      copy.querySelectorAll('img, figcaption, br, a:empty').forEach(n => n.remove());
      return !copy.textContent.trim();
    };
    const runs = [];
    let run = null;
    for (const el of [...box.children]) {
      if (!pictureOnly(el)) { run = null; continue; }
      if (!run) { run = []; runs.push(run); }
      run.push(el);
    }
    for (const blocks of runs) {
      const imgs = blocks.flatMap(b => [...b.querySelectorAll('img')]);
      const captions = blocks.flatMap(b => [...b.querySelectorAll('figcaption')]).map(c => c.innerHTML.trim()).filter(Boolean);
      const spread = document.createElement('figure');
      spread.className = 'mh-plates' + (imgs.length === 1 ? ' mh-plates--one' : '');
      const grid = document.createElement('div');
      grid.className = 'mh-plates__grid';
      imgs.forEach((img, i) => {
        // a print is small: ask Shopify's image CDN for the size it is shown at, not the full upload (lighter to
        // download and to draw while the paper unfolds). The post's images wait to load until the entry opens.
        try {
          const u = new URL(img.currentSrc || img.src, location.href);
          if (!img.srcset && (u.hostname === 'cdn.shopify.com' || u.pathname.startsWith('/cdn/shop/'))) {
            img.srcset = [360, 540, 720, 1080].map(w => { u.searchParams.set('width', w); return `${u.href} ${w}w`; }).join(', ');
            img.sizes = imgs.length === 1 ? '(max-width: 699px) 88vw, 900px' : '(max-width: 699px) 44vw, 470px';
          }
        } catch (e) {}
        const plate = document.createElement('span');
        plate.className = 'mh-plate';
        plate.style.setProperty('--r', [-.9, .8, .5, -.6][i % 4] + 'deg');
        const link = img.closest('a');
        plate.append(link && blocks.some(b => b.contains(link)) ? link : img);
        grid.append(plate);
      });
      spread.append(grid);
      if (captions.length) {
        const cap = document.createElement('figcaption');
        cap.innerHTML = captions.join(' ');
        spread.append(cap);
      }
      blocks[0].before(spread);
      blocks.forEach(b => b.remove());
    }
  }

  class MenasaJournal extends HTMLElement {
    // After the router's swap (it scrolls to the top once the page is in).
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.mhOn) return;
        this.mhOn = true;
        this.off = [];
        const page = this.classList.contains('mh-article');
        const desk = this.classList.contains('mh-journal--desk');
        if (desk) {
          this.querySelectorAll('.mh-entry__content img').forEach(img => {
            if (!img.hasAttribute('loading')) img.loading = 'lazy';
          });
          this.querySelectorAll('.mh-entry__content').forEach(deskPlates);
          this.setAttribute('data-desk-ready', '');
          this.querySelectorAll('.mh-entry').forEach(li => {
            if (li.querySelector('.mh-desk__lift')) return;
            // the letter is laid out once inside a frame (the well) whose height alone animates; the rest of the
            // letter becomes its own strip of paper (the fold) that can swing down from the crease
            const card = li.querySelector('.mh-entry__card'), more = li.querySelector('.mh-entry__more');
            if (card) {
              const well = document.createElement('div');
              well.className = 'mh-desk__well';
              card.before(well);
              well.append(card);
            }
            if (more) {
              const fold = document.createElement('div'), shade = document.createElement('span');
              fold.className = 'mh-desk__fold';
              shade.className = 'mh-desk__shade';
              shade.setAttribute('aria-hidden', 'true');
              fold.append(shade, ...more.childNodes);
              more.append(fold);
            }
            const shadow = document.createElement('span');
            shadow.className = 'mh-desk__lift';
            shadow.setAttribute('aria-hidden', 'true');
            // the brass clip as a real element (Safari can't animate a pseudo-element)
            const clip = document.createElement('span');
            clip.className = 'mh-desk__clip';
            clip.setAttribute('aria-hidden', 'true');
            li.append(shadow, clip);
          });
          this.on_(document, 'shopify:section:unload', e => {
            if (e.target.contains(this)) this.disconnectedCallback();
          });
          this.on_(reduce, 'change', () => {
            if (reduce.matches) this.querySelectorAll('.mh-entry').forEach(li => li.mhDesk?.settle());
          });
          this.on_(window, 'resize', () => this.querySelectorAll('.mh-entry').forEach(li => li.mhDesk?.settle()));
        } else {
          if (!reduce.matches) this.querySelectorAll('.mh-entry__content').forEach(box => prepareContent(box, page));
          reveal(this);
        }
        if (!page) this.entries();
      });
    }

    disconnectedCallback() {
      this.querySelectorAll('.mh-entry').forEach(li => li.mhDesk?.settle());
      (this.off || []).forEach(f => f());
      this.off = [];
      this.mhOn = false;
    }

    on_(target, type, fn, opts) { target.addEventListener(type, fn, opts); this.off.push(() => target.removeEventListener(type, fn, opts)); }

    entries() {
      const items = [...this.querySelectorAll('.mh-entry')];
      for (const li of items) {
        const toggle = li.querySelector('.mh-entry__toggle');
        if (!toggle || !li.querySelector('.mh-entry__more')) continue;
        this.on_(toggle, 'click', () => this.flip(li, !li.classList.contains('is-open')));
        // The title and the cover open the entry here (without the script they lead to its own page).
        li.querySelectorAll('[data-entry-open]').forEach(a => this.on_(a, 'click', e => {
          if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          if (!li.classList.contains('is-open')) this.flip(li, true);
        }));
        li.querySelectorAll('[data-entry-close]').forEach(b => this.on_(b, 'click', () => {
          this.flip(li, false);
          toggle.focus({ preventScroll: true });
        }));
      }
      // A link straight to an entry (/pages/about#entry-…) arrives with it open.
      const hash = decodeURIComponent(location.hash.slice(1));
      const target = hash && items.find(li => li.id === hash);
      if (target) {
        this.flip(target, true, true);
        requestAnimationFrame(() => {
          const header = document.getElementById('PrestigeHeader');
          scrollTo({ top: target.getBoundingClientRect().top + scrollY - (header ? header.offsetHeight : 72) - 16, behavior: 'auto' });
        });
      }
    }

    // Opens or closes an entry. Opening: the cover glides from beside the title to its place as the lead picture while
    // the entry grows, then the words and pictures rise in. Closing: they fade, the cover glides back, the entry folds,
    // and if its top had scrolled away it comes back into view.
    flip(li, open, instant) {
      if (this.classList.contains('mh-journal--desk')) { this.flipDesk(li, open, instant); return; }
      // A close still gliding back to the entry's top: opening again simply keeps it open.
      if (li.mhCancel) { li.mhCancel(); li.mhCancel = null; if (open) return; }
      if (li.classList.contains('is-open') === open && !li.classList.contains('is-closing')) return;
      const more = li.querySelector('.mh-entry__more'), toggle = li.querySelector('.mh-entry__toggle');
      const cover = li.querySelector('.mh-entry__cover .mh-sweep');
      (li.mhAnims || []).forEach(a => a.finish());
      clearTimeout(li.mhTimer);
      const quiet = instant || reduce.matches;
      const go = () => {
        const h0 = li.offsetHeight, r0 = cover && cover.getBoundingClientRect();
        li.classList.toggle('is-open', open);
        li.classList.remove('is-closing');
        more.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        if (quiet) {
          li.classList.toggle('is-shown', open);
          return;
        }
        const h1 = li.offsetHeight, r1 = cover && cover.getBoundingClientRect();
        const timing = { duration: pace(950), easing: SWEEP };
        li.classList.add('is-moving');
        const anims = [li.animate([{ height: h0 + 'px' }, { height: h1 + 'px' }], timing)];
        if (cover && r0.width && r1.width) {
          anims.push(cover.animate([
            { transformOrigin: '0 0', transform: `translate(${(r0.left - r1.left).toFixed(1)}px,${(r0.top - r1.top).toFixed(1)}px) scale(${(r0.width / r1.width).toFixed(4)})` },
            { transformOrigin: '0 0', transform: 'none' }
          ], timing));
        }
        li.mhAnims = anims;
        Promise.all(anims.map(a => a.finished.catch(() => {}))).then(() => {
          if (li.mhAnims === anims) { li.mhAnims = null; li.classList.remove('is-moving'); }
        });
        if (open) li.mhTimer = setTimeout(() => li.classList.add('is-shown'), pace(240));
      };
      if (open || quiet) { go(); return; }
      const fold = () => {
        li.classList.add('is-closing');
        li.classList.remove('is-shown');
        li.mhTimer = setTimeout(() => { go(); keepInView(li); }, pace(300));
      };
      // Closed from further down: glide back to the entry's top first, its words still showing, then fold.
      const header = document.getElementById('PrestigeHeader'), top = header ? header.offsetHeight : 72;
      const r = li.getBoundingClientRect();
      if (r.top >= top) { fold(); return; }
      let folded = false;
      const done = () => {
        if (folded) return;
        folded = true;
        li.mhCancel = null;
        removeEventListener('scrollend', done);
        clearTimeout(li.mhTimer);
        fold();
      };
      li.mhCancel = () => { folded = true; removeEventListener('scrollend', done); clearTimeout(li.mhTimer); };
      addEventListener('scrollend', done, { once: true });
      li.mhTimer = setTimeout(done, Math.min(1500, 450 + (top - r.top) * .15));
      scrollTo({ top: r.top + scrollY - top - 16, behavior: 'smooth' });
    }

    /* Desk only (10 Oct 2026, the approved study): the brass clip is knocked loose and swings aside, the paper
       straightens and lifts, and the rest of the letter unfolds from the crease: the flap's angle follows the length it
       shows, so the fold and the paper agree; the prints drop onto it one by one. Every moving part is sampled from one
       timeline, and closing plays the same timeline backwards at the same pace. The frame around the sheet (the well)
       really changes height, so the page length, the sheets below and the shadow all follow every frame; the visitor
       is never scrolled anywhere. A tap mid-way reverses it. */
    flipDesk(li, open, instant) {
      const run = li.mhDesk;
      const more = li.querySelector('.mh-entry__more');
      const toggle = li.querySelector('.mh-entry__toggle');
      if (run) {
        if (run.open === open) return;
        run.open = open;
        toggle.setAttribute('aria-expanded', String(open));
        more.inert = !open;
        run.anims.forEach(a => a.reverse());
        return;
      }
      if (li.classList.contains('is-open') === open) return;
      const layout = on => { li.classList.toggle('is-open', on); li.classList.toggle('is-shown', on); more.hidden = !on; };
      toggle.setAttribute('aria-expanded', String(open));
      const well = li.querySelector('.mh-desk__well');
      if (instant || reduce.matches || !li.animate || !well) {
        layout(open);
        if (!open && !instant) {
          const top = document.getElementById('PrestigeHeader')?.offsetHeight || 72, r = li.getBoundingClientRect();
          if (r.top < top) scrollTo({ top: r.top + scrollY - top - 16, behavior: 'instant' });
        }
        return;
      }
      const light = root.dataset.effects === 'light';
      const scroll0 = scrollY;
      layout(false); const h0 = well.offsetHeight;
      layout(true);  const h1 = well.offsetHeight;
      if (scrollY !== scroll0) scrollTo({ top: scroll0, behavior: 'instant' });
      const gap = Math.max(0, h1 - h0);
      const fold = li.querySelector('.mh-desk__fold'), shade = li.querySelector('.mh-desk__shade');
      const tilt = getComputedStyle(li).getPropertyValue('--desk-tilt').trim() || '0deg';
      // a long letter takes a little longer, so its paper never moves faster than about 40 px a frame
      const D = light ? Math.min(900, 520 + gap * .2) : Math.min(1700, Math.max(1100, 760 + gap * .45));
      const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const out = t => 1 - Math.pow(1 - t, 3);
      const span = (t, a, b) => Math.min(1, Math.max(0, (t - a) / (b - a)));
      const U0 = light ? 0 : .16, U1 = light ? 1 : .86, STEPS = 24;
      const liF = [], wellF = [], foldF = [], shadeF = [];
      for (let i = 0; i <= STEPS; i++) {
        const t = i / STEPS, s = ease(span(t, U0, U1)), l = out(span(t, 0, .3));
        const hidden = gap * (1 - s);
        if (!light) liF.push({ offset: t, transform: `rotate(calc(${tilt} * ${(1 - l).toFixed(3)})) translateY(${(-3 * l).toFixed(2)}px) scale(${(1 + .006 * l).toFixed(4)})` });
        // the real height: the page below, its length and the paper's shadow all follow it every frame
        wellF.push({ offset: t, height: (h1 - hidden).toFixed(1) + 'px' });
        if (!light && fold) {
          // projected length = cos of the angle, so the flap shows exactly the length the frame has opened
          const angle = Math.acos(Math.min(1, s)) * 180 / Math.PI;
          foldF.push({ offset: t, transform: `perspective(2600px) rotateX(${angle.toFixed(2)}deg)` });
          shadeF.push({ offset: t, opacity: (angle / 90 * .9).toFixed(3) });
        }
      }
      const anims = [];
      const add = (el, frames) => { if (el && frames.length) anims.push(el.animate(frames, { duration: D, easing: 'linear', fill: 'both' })); };
      add(li, liF);
      add(well, wellF);
      add(fold, foldF);
      add(shade, shadeF);
      if (!light) {
        // the brass clip is knocked loose, swings, and is left hanging off the corner (its open pose is in the stylesheet)
        add(li.querySelector('.mh-desk__clip'), [
          { offset: 0, transform: 'none', easing: 'cubic-bezier(.3,0,.3,1)' },
          { offset: .12, transform: 'translate(3px,-16px) rotate(-18deg)', easing: 'cubic-bezier(.2,.6,.3,1)' },
          { offset: .32, transform: 'translate(14px,-2px) rotate(34deg)', easing: 'cubic-bezier(.45,0,.4,1)' },
          { offset: .46, transform: 'translate(11px,-5px) rotate(17deg)', easing: 'ease-in-out' },
          { offset: .58, transform: 'translate(12px,-4px) rotate(26deg)', easing: 'ease-in-out' },
          { offset: .7, transform: 'translate(12px,-4px) rotate(21deg)' },
          { offset: 1, transform: 'translate(12px,-4px) rotate(23deg)' }
        ]);
        add(li.querySelector('.mh-desk__lift'), [{ offset: 0, opacity: 0 }, { offset: .3, opacity: 1 }, { offset: 1, opacity: 1 }]);
        // each print is dropped onto the paper once it has unfolded
        [...more.querySelectorAll('.mh-plate')].slice(0, 8).forEach((el, i) => {
          const a = Math.min(.55 + i * .07, .8), from = `translateY(-16px) rotate(${i % 2 ? 2.6 : -2.4}deg) scale(1.04)`;
          add(el, [
            { offset: 0, opacity: 0, transform: from }, { offset: a, opacity: 0, transform: from, easing: 'cubic-bezier(.3,.8,.35,1.2)' },
            { offset: Math.min(a + .18, 1), opacity: 1, transform: 'none' }, { offset: 1, opacity: 1, transform: 'none' }
          ]);
        });
      }
      const runState = { open, anims };
      li.mhDesk = runState;
      more.inert = !open;
      li.classList.add('is-desk-moving');
      root.classList.add('mh-desk-moving');
      if (!open) anims.forEach(a => { a.currentTime = D; a.playbackRate = -1; });
      runState.settle = () => {
        if (li.mhDesk !== runState) return;
        li.mhDesk = null;
        anims.forEach(a => a.cancel());
        layout(runState.open);
        more.inert = false;
        li.classList.remove('is-desk-moving');
        if (!document.querySelector('.is-desk-moving')) root.classList.remove('mh-desk-moving');
      };
      // a reversal re-arms the animations; settle once every one has truly stopped
      anims.forEach(a => a.addEventListener('finish', () => { if (anims.every(x => x.playState === 'finished')) runState.settle(); }));
    }
  }
  customElements.define('menasa-heritage', MenasaHeritage);
  if (!customElements.get('menasa-journal')) customElements.define('menasa-journal', MenasaJournal);
})();
